import React, { useEffect, useState } from "react";
import { Check, Sparkles, Tag } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const FEATURES = {
  basic_viewer: ["Full on-demand library", "Follow creators", "Watch live streams", "HD streaming"],
  premium_viewer: [
    "Everything in Basic",
    "Ad-free viewing across the platform",
    "Early access to premieres (48h before public)",
    "Exclusive premium-only row on Home",
    "4K streaming where available",
    "Vote in Creator Awards with 2× weight",
  ],
  creator: ["Upload & publish content", "Public creator profile", "Basic analytics", "Revenue sharing", "Tips enabled"],
  premium_creator: ["Everything in Creator", "Go Live + Director's Mode", "Pay-per-view monetization", "Creator ad sales tools", "Advanced analytics", "Priority placement", "Referral bonus (10%)"],
};

export default function Subscribe() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoErr, setPromoErr] = useState("");

  useEffect(() => { api.get("/payments/tiers").then((r) => setTiers(r.data)); }, []);

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoErr("");
    try {
      const { data } = await api.get(`/subscription-promos/validate/${code}`);
      setPromo(data);
      toast.success(`Promo applied: ${data.discount_type === "pct" ? data.value + "% off" : "$" + data.value + " off"}`);
    } catch (e) {
      setPromo(null);
      setPromoErr(e?.response?.data?.detail || "Invalid promo");
    }
  };

  const clearPromo = () => { setPromo(null); setPromoInput(""); setPromoErr(""); };

  const checkout = async (tierId) => {
    if (!user) { toast.info("Sign in first"); return nav("/login"); }
    setBusy(tierId);
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post("/payments/checkout", { tier: tierId, origin_url, promo_code: promo?.code });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Checkout failed");
      setBusy(null);
    }
  };

  return (
    <div data-testid="subscribe-page" className="pt-32 pb-24 px-4 sm:px-8 md:px-12 max-w-7xl mx-auto">
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-semibold">Pricing</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl text-white mt-3 tracking-tighter">
          Choose your <span className="text-tv-brand">plan</span>
        </h1>
        <p className="mt-4 text-white/70">Watch, upload, or go live. Cancel anytime.</p>

        {/* Promo code box */}
        <div className="mt-6 max-w-md mx-auto" data-testid="promo-code-section">
          {!promo ? (
            <div className="flex gap-2">
              <input
                data-testid="promo-code-input"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                placeholder="Have a promo code?"
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm font-mono tracking-wider uppercase outline-none focus:border-tv-accent"
              />
              <button
                data-testid="promo-apply-btn"
                onClick={applyPromo}
                disabled={!promoInput.trim()}
                className="bg-white/10 hover:bg-white/20 disabled:opacity-50 text-white font-bold px-4 rounded-lg text-sm flex items-center gap-1.5"
              >
                <Tag className="w-4 h-4" /> Apply
              </button>
            </div>
          ) : (
            <div data-testid="promo-applied" className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-4 py-2.5">
              <Tag className="w-4 h-4 text-emerald-400" />
              <div className="flex-1 text-left text-sm">
                <span className="font-bold text-emerald-300 font-mono">{promo.code}</span>
                <span className="text-white/80 ml-2">{promo.discount_type === "pct" ? `${promo.value}% off` : `$${promo.value} off`}</span>
                {promo.description && <span className="text-white/50 ml-2 text-xs">· {promo.description}</span>}
              </div>
              <button onClick={clearPromo} data-testid="promo-clear-btn" className="text-white/60 hover:text-white text-xs">Remove</button>
            </div>
          )}
          {promoErr && <div data-testid="promo-error" className="text-red-400 text-xs mt-2">{promoErr}</div>}
        </div>
      </div>

      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {tiers.map((t) => {
          const isPremium = t.id === "premium_creator";
          return (
            <div
              key={t.id}
              data-testid={`tier-card-${t.id}`}
              className={`tv-glass rounded-2xl p-7 flex flex-col relative transition hover:-translate-y-2 ${isPremium ? "border-tv-accent/60 shadow-[0_0_40px_rgba(250,204,21,0.15)]" : ""}`}
            >
              {isPremium && (
                <div className="absolute -top-3 left-6 bg-tv-accent text-black text-[10px] font-bold uppercase px-2 py-1 rounded flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Most Powerful
                </div>
              )}
              <div className="text-xs uppercase tracking-wider text-white/50 font-semibold">{t.role.replace("_", " ")}</div>
              <div className="font-display font-black text-3xl text-white mt-2 tracking-tight">{t.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-5xl font-black text-white">${t.price}</span>
                <span className="text-white/50 text-sm">/month</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm text-white/80 flex-1">
                {FEATURES[t.id]?.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-tv-brand mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`tier-subscribe-${t.id}`}
                disabled={busy === t.id}
                onClick={() => checkout(t.id)}
                className={`mt-6 w-full py-3 rounded-full font-bold transition ${isPremium ? "bg-tv-accent text-black hover:bg-yellow-400" : "tv-glass text-white hover:bg-white/10 border border-white/20"} disabled:opacity-60`}
              >
                {busy === t.id ? "Redirecting…" : "Subscribe"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
